import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { readCsrfCookie, CSRF_HEADER, needsCsrf } from '@/api/csrf';
import type { CsvImportResult } from '@/api/types';
import { Button } from '@/components/ui/Button';
import { useProjectContext } from '@/project/ProjectContext';
import { downloadLeadsImportTemplate, excelFileToCsvText } from './leadImportTemplate';
import styles from './Leads.module.css';

export function CsvImportExport() {
  const { selectedProjectId } = useProjectContext();
  const qc = useQueryClient();
  const [report, setReport] = useState<CsvImportResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [importMode, setImportMode] = useState<'insert' | 'upsert'>('insert');
  const [templateBusy, setTemplateBusy] = useState(false);

  const projectId = selectedProjectId;

  const importCsv = useMutation({
    mutationFn: async (file: File) => {
      if (!projectId) throw new Error('Kies een project');
      let uploadFile = file;
      const lower = file.name.toLowerCase();
      if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) {
        const csvText = await excelFileToCsvText(file);
        uploadFile = new File([csvText], 'import.csv', { type: 'text/csv' });
      }
      const fd = new FormData();
      fd.append('file', uploadFile);
      fd.append('project_id', projectId);
      fd.append('mode', importMode);
      const headers = new Headers();
      if (needsCsrf('POST')) {
        const token = readCsrfCookie();
        if (token) headers.set(CSRF_HEADER, token);
      }
      setBusy(true);
      const res = await fetch('/api/leads/import', {
        method: 'POST',
        body: fd,
        credentials: 'include',
        headers,
      });
      setBusy(false);
      if (!res.ok) {
        const err = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(err?.error ?? 'Import mislukt');
      }
      return (await res.json()) as CsvImportResult;
    },
    onSuccess: (data) => {
      setReport(data);
      void qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });

  function exportCsv() {
    const q = new URLSearchParams();
    if (projectId) q.set('project_id', projectId);
    window.open(`/api/leads/export.csv?${q.toString()}`, '_blank', 'noopener,noreferrer');
  }

  async function onDownloadTemplate() {
    setTemplateBusy(true);
    try {
      await downloadLeadsImportTemplate();
    } finally {
      setTemplateBusy(false);
    }
  }

  return (
    <section className={styles.bulkCard} aria-label="Import en export">
      <div className={styles.bulkCardHead}>
        <div>
          <h2 className={styles.bulkTitle}>Bulk &amp; export</h2>
          <p className={styles.bulkHint}>
            Download het Excel-sjabloon, vul leads in en importeer als CSV of .xlsx.
          </p>
        </div>
        <div className={styles.bulkHeadActions}>
          <Button
            type="button"
            variant="secondary"
            disabled={!projectId || templateBusy}
            onClick={() => void onDownloadTemplate()}
          >
            {templateBusy ? 'Bezig…' : 'Excel-sjabloon'}
          </Button>
          <Button type="button" variant="secondary" onClick={exportCsv} disabled={!projectId}>
            Export CSV
          </Button>
        </div>
      </div>
      {!projectId ? (
        <p className={styles.muted}>Selecteer een project om te importeren.</p>
      ) : (
        <div className={styles.bulkRow}>
          <label className={styles.filterField}>
            <span className={styles.filterLabel}>Importmodus</span>
            <select
              className={styles.filterInput}
              value={importMode}
              onChange={(e) => setImportMode(e.target.value as 'insert' | 'upsert')}
            >
              <option value="insert">Alleen nieuw (skip duplicaat)</option>
              <option value="upsert">Upsert op e-mail</option>
            </select>
          </label>
          <label className={styles.filterField}>
            <span className={styles.filterLabel}>Bestand (CSV of Excel)</span>
            <input
              className={styles.fileInput}
              type="file"
              accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              disabled={busy || importCsv.isPending}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) importCsv.mutate(f);
                e.target.value = '';
              }}
            />
          </label>
        </div>
      )}
      {busy || importCsv.isPending ? <p className={styles.muted}>Import bezig…</p> : null}
      {importCsv.isError ? (
        <p className={styles.formError} role="alert">
          {(importCsv.error as Error).message}
        </p>
      ) : null}
      {report ? (
        <div className={styles.report}>
          <p>
            Aangemaakt: {report.summary.created} · Bijgewerkt: {report.summary.updated ?? 0} ·
            Overgeslagen: {report.summary.skipped} · Fouten: {report.summary.errors}
          </p>
          <ul>
            {report.report.slice(0, 50).map((r) => (
              <li key={`${r.row}-${r.status}`}>
                Rij {r.row}: {r.status}
                {r.reason ? ` — ${r.reason}` : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
